import { useEffect, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import PageHeader from "../../components/PageHeader";

type Room = {
  code: string;
  hostId: string;
  hostName: string;
  guestId?: string;
  guestName?: string;
  status: string;
  isPublic?: boolean;
  hostScore: number;
  guestScore: number;
  roundNumber: number;
  currentWord: string;
  createdAtMs: number;
  turnStartedAtMs?: number;
};

export default function Rooms() {
  const [status, setStatus] = useState<"waiting" | "playing" | "finished">(
    "playing"
  );
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      query(
        collection(db, "rooms"),
        where("status", "==", status),
        orderBy("createdAt", "desc"),
        limit(50)
      ),
      (snap) => {
        setRooms(
          snap.docs.map((d) => {
            const x = d.data();
            return {
              code: (x.code as string) ?? d.id,
              hostId: (x.hostId as string) ?? "",
              hostName: (x.hostName as string) ?? "—",
              guestId: x.guestId as string | undefined,
              guestName: x.guestName as string | undefined,
              status: (x.status as string) ?? "—",
              isPublic: Boolean(x.isPublic),
              hostScore: Number(x.hostScore ?? 0),
              guestScore: Number(x.guestScore ?? 0),
              roundNumber: Number(x.roundNumber ?? 1),
              currentWord: (x.currentWord as string) ?? "",
              createdAtMs: x.createdAt?.toMillis?.() ?? 0,
              turnStartedAtMs: x.turnStartedAt?.toMillis?.(),
            };
          })
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return unsub;
  }, [status]);

  return (
    <div>
      <PageHeader
        title="Odalar"
        subtitle="Canlı oyun durumu"
        actions={
          <div className="inline-flex rounded-xl bg-surface border border-stroke p-1">
            <TabBtn
              active={status === "waiting"}
              onClick={() => setStatus("waiting")}
            >
              Bekleyen
            </TabBtn>
            <TabBtn
              active={status === "playing"}
              onClick={() => setStatus("playing")}
            >
              Oynanan
            </TabBtn>
            <TabBtn
              active={status === "finished"}
              onClick={() => setStatus("finished")}
            >
              Biten
            </TabBtn>
          </div>
        }
      />

      {loading ? (
        <div className="card p-8 text-textMuted">Yükleniyor…</div>
      ) : rooms.length === 0 ? (
        <div className="card p-10 text-center text-textMuted">
          Bu durumda oda yok.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rooms.map((r) => (
            <div key={r.code} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-extrabold text-gold tracking-widest">
                    #{r.code}
                  </span>
                  {r.isPublic && (
                    <span className="pill bg-diamond/15 text-diamond border border-diamond/40 text-[10px]">
                      rastgele
                    </span>
                  )}
                </div>
                <span className="text-xs text-textMuted">
                  {timeAgo(r.createdAtMs)}
                </span>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Side
                  name={r.hostName}
                  score={r.hostScore}
                  active={status === "playing"}
                />
                <div className="text-textMuted text-xs font-extrabold tracking-widest">
                  VS
                </div>
                <Side
                  name={r.guestName ?? "—"}
                  score={r.guestScore}
                  active={status === "playing"}
                  ghost={!r.guestId}
                />
              </div>

              {r.currentWord && (
                <div className="mt-3 text-center text-sm text-textMuted">
                  son kelime:{" "}
                  <span className="text-textPrimary font-semibold">
                    {r.currentWord.toUpperCase()}
                  </span>{" "}
                  · tur {r.roundNumber}
                </div>
              )}

              {r.turnStartedAtMs && status === "playing" && (
                <div className="mt-1 text-center text-[10px] text-textMuted">
                  son hamle {timeAgo(r.turnStartedAtMs)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Side({
  name,
  score,
  ghost,
  active,
}: {
  name: string;
  score: number;
  ghost?: boolean;
  active?: boolean;
}) {
  return (
    <div
      className={[
        "flex-1 rounded-xl p-3 text-center border",
        ghost
          ? "border-stroke bg-surface/40 text-textMuted"
          : active
          ? "border-primarySoft/40 bg-primary/10"
          : "border-stroke bg-surfaceHigh",
      ].join(" ")}
    >
      <div className="font-extrabold truncate">{name}</div>
      <div className="text-2xl font-extrabold text-gold">{score}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 py-1.5 rounded-lg text-xs font-bold transition",
        active
          ? "bg-primary text-white shadow"
          : "text-textMuted hover:text-textPrimary",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function timeAgo(ms: number) {
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}sn önce`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}sa önce`;
  return `${Math.floor(h / 24)}g önce`;
}
